import {
  GROUPING_SIZE_DEFAULT,
  GROUPING_SIZE_MAXIMUM_DEFAULT,
  GROUPING_SIZE_MINIMUM_DEFAULT,
} from '@breakoutlearning/firebase-repository/constants'
import type { InstructorSlideDeckCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSlideDeckCubit'
import {
  AssignmentGroupingType,
  type SectionAssignment,
} from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { calculateInitialExpiresAt } from '@breakoutlearning/firebase-repository/sources-of-truth/initialExpiresAt'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import {
  dateTime,
  noTryAsync,
} from '@breakoutlearning/firebase-repository/util'
import { zodResolver } from '@hookform/resolvers/zod'
import { SectionSelect } from 'components/SectionSelect'
import { ConfirmationDialog } from 'components/dialogs/ConfirmationDialog'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { useRepository, useFunctions } from 'hooks/auth'
import { useBreakoutUser } from 'hooks/profile'
import { ConfigureCustomRubrics } from 'pages/instructor/assignment/dialogs/ConfigureCustomRubrics'
import {
  isRubricEligibleForCustomGrading,
  type CustomRubricsConfig,
} from 'pages/instructor/assignment/dialogs/customRubricsConfig'
import { saveCustomRubrics } from 'pages/instructor/slide_deck/AssignmentDialog/saveCustomRubrics'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import type {
  AssignmentFormValues,
  DraftAssignmentFormValues,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import {
  getAssignmentSchema,
  getDraftAssignmentSchema,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { roundMinutesDownTo15 } from 'util/roundMinutesUpTo15'
import { BaseAssignmentDialog } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog'
import { useDialogs } from 'hooks/dialogs'
import {
  defaultPeerReviewCriteria,
  calculateDBDatesFromFormDates,
} from 'pages/instructor/slide_deck/AssignmentDialog/helpers'
import { DateTime } from 'luxon'

export const CreateAssignmentDialogGrouping = observer(
  function CreateAssignmentDialogGrouping({
    onCreate,
    cubit,
  }: {
    onCreate?: () => void
    cubit: InstructorSlideDeckCubit
  }) {
    const store = useRootStore()
    const { tt } = useTranslationTyped()
    const { customRubricsEnabled, useApiForCustomRubrics } =
      useRepository().featureFlags.data
    const functions = useFunctions()
    const user = useBreakoutUser()
    const scalarsRef =
      useRef<SectionAssignment['data']['assignmentGradingScalars']>(undefined)
    const customRubricsRef = useRef<CustomRubricsConfig>({
      enabled: false,
      isValid: true,
      rubricConfigs: [],
    })
    const canConfigureCustomRubrics =
      customRubricsEnabled &&
      cubit.rubrics.models.some(isRubricEligibleForCustomGrading)
    const [customRubricsValid, setCustomRubricsValid] = useState(true)
    const [customRubricsDirty, setCustomRubricsDirty] = useState(false)
    const [weightsDialogOpen, setWeightsDialogOpen] = useState(false)

    const persistCustomRubrics = useCallback(
      async (sectionId: string, assignmentId: string) => {
        const config = customRubricsRef.current
        if (!config.enabled) return
        await saveCustomRubrics({
          useApi: useApiForCustomRubrics,
          functions,
          sectionId,
          assignmentId,
          rubricConfigs: config.rubricConfigs,
        })
      },
      [functions, useApiForCustomRubrics]
    )

    const hasQuestions = cubit.questions.models.some((q) => q.isGraded)
    const hasRubrics = cubit.rubrics.models.length > 0

    const schema = useMemo(() => {
      return getAssignmentSchema({
        tt,
        sectionStartDate: cubit.section.data.sectionStartDate,
        sectionEndDate: cubit.section.data.sectionEndDate,
      })
    }, [
      cubit.section.data.sectionEndDate,
      cubit.section.data.sectionStartDate,
      tt,
    ])

    const queryParams = store.router.queryParams

    const dueDateDefaultValue = (() => {
      const rawValue = dateTime(cubit.assignmentWithLatestExpiresAt.dueDate)
      if (!rawValue) return undefined

      if (rawValue < DateTime.now()) return undefined

      const sectionEndDate = dateTime(cubit.section.data.sectionEndDate)
      if (sectionEndDate && rawValue > sectionEndDate) return undefined

      return rawValue
    })()

    const assignedAtDefaultValue = DateTime.max(
      dateTime(cubit.section.data.sectionStartDate) ?? DateTime.now(),
      DateTime.now()
    )

    const form = useForm<AssignmentFormValues>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      defaultValues: {
        catalogId: queryParams?.catalogId?.toString() || undefined,
        assignmentType: AssignmentType.ManyGroups,
        groupingType: AssignmentGroupingType.manual,
        sectionId: queryParams?.sectionId?.toString() || undefined,
        assignedAt: assignedAtDefaultValue,
        dueDate: dueDateDefaultValue,
        allowLateSubmissions: false,
        lateSubmissionCutoff: undefined,
        groupingSize: GROUPING_SIZE_DEFAULT,
        groupingSizeMaximum: GROUPING_SIZE_MAXIMUM_DEFAULT,
        groupingSizeMinimum: GROUPING_SIZE_MINIMUM_DEFAULT,
        configureGrading: false,
        forbidStudentGroupCreation: false,
        forbidStudentGroupScheduling: false,
        forbidStudentGroupSelection: false,
        forbidStudentGroupJoining: false,
        forbidStudentGroupLeaving: false,
        peerReviewsEnabled: false,
        peerReviewCriteria: defaultPeerReviewCriteria(tt),
        peerReviewExcludesSelfReview: false,
        peerReviewsHiddenFromReviewees: false,
        showFinalGradeToParticipants: true, // true for new assignments
      },
    })

    // Default the grading toggle to "Yes" when the instructor's saved defaults
    // opt into assigning a final grade. Applied once, and only if the instructor
    // hasn't already toggled grading themselves (the value is fetched async, so a
    // choice made before it resolves must win).
    const gradeDefaultAppliedRef = useRef(false)
    const gradingTouchedRef = useRef(false)
    useEffect(() => {
      const subscription = form.watch((_values, { name }) => {
        if (name === 'configureGrading' && !gradeDefaultAppliedRef.current) {
          gradingTouchedRef.current = true
        }
      })
      return () => subscription.unsubscribe()
    }, [form])
    useEffect(() => {
      let active = true
      user
        .fetchScoreMappingDefaults()
        .then((doc) => {
          if (!active || gradeDefaultAppliedRef.current) return
          gradeDefaultAppliedRef.current = true
          if (doc.assignFinalGrade && !gradingTouchedRef.current) {
            form.setValue('configureGrading', true)
          }
        })
        .catch(() => {})
      return () => {
        active = false
      }
    }, [user, form])

    const [{ gradingSumValid, gradingSum }, setGradingSum] = useState<{
      gradingSumValid: boolean
      gradingSum: number
    }>({ gradingSumValid: false, gradingSum: 0 })

    const setGradingSumCallback = useCallback(
      ({ isValid, sumAsInt }: { isValid: boolean; sumAsInt: number }) => {
        setGradingSum({
          gradingSum: sumAsInt,
          gradingSumValid: isValid,
        })
      },
      []
    )

    const sectionIdFromForm = form.watch('sectionId')

    // I love how I yap about useEffect being bad and then I do this
    // But that's how it has to work - the change to sectionId in the form
    // must be reflected in the cubit
    useEffect(() => {
      cubit.setSectionId(sectionIdFromForm)
    }, [cubit, sectionIdFromForm])

    const assignmentWithLatestExpiresAt = cubit.assignmentWithLatestExpiresAt

    // we need to destructure here so that useMemo cache gets invalidated
    // when assignmentWithLatestExpiresAt changes
    const dueDateInitialViewRaw = useMemo(() => {
      return calculateInitialExpiresAt({
        section: cubit.section,
        assignment: assignmentWithLatestExpiresAt,
      })
      // thie memo must cache bust when assignmentWithLatestExpiresAt data changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      assignmentWithLatestExpiresAt,
      assignmentWithLatestExpiresAt.data,
      cubit.section.data,
    ])

    const dueDateInitialView = useMemo(() => {
      return dueDateInitialViewRaw
        ? roundMinutesDownTo15(dueDateInitialViewRaw)
        : undefined
    }, [dueDateInitialViewRaw])

    const submit = useCallback(
      async (data: AssignmentFormValues) => {
        if (
          customRubricsRef.current.enabled &&
          !customRubricsRef.current.isValid
        ) {
          toast.error(tt.instructor_assignment.save_failed())
          return
        }
        const assignmentGradingScalars = data.configureGrading
          ? scalarsRef.current
          : undefined

        const { expiresAt, assignmentOverdueAt } =
          calculateDBDatesFromFormDates({
            dueDate: data.dueDate,
            allowLateSubmissions: data.allowLateSubmissions,
            lateSubmissionCutoff: data.allowLateSubmissions
              ? data.lateSubmissionCutoff
              : undefined,
          })

        const [assignmentId, error] = await noTryAsync(async () => {
          // At this point, form validation ensures assignedAt and expiresAt are defined
          if (!data.assignedAt || !expiresAt) {
            throw new Error('assignedAt and expiresAt are required')
          }

          return await cubit.createSectionAssignment({
            slideDeckId: cubit.slideDeckId,
            assignmentGradingScalars,
            ...data,
            expiresAt,
            assignmentOverdueAt,
            peerReviewExcludesSelfReview: data.peerReviewExcludesSelfReview,
            peerReviewsHiddenFromReviewees: data.peerReviewsHiddenFromReviewees,
          })
        })

        if (error) {
          toast.error(tt.instructor_assignment.save_failed())
          throw error
        }

        if (assignmentId) {
          if (data.peerReviewsEnabled) {
            const questions = (data.peerReviewCriteria ?? []).map(
              (c, index) => ({
                prQuestion: c.prQuestion,
                prDescription: c.prDescription,
                prOrder: index,
              })
            )

            await cubit.setPeerReviewQuestionsForDraftAssignment({
              sectionId: data.sectionId,
              assignmentId,
              questions,
            })
          }
          const [, rubricErr] = await noTryAsync(() =>
            persistCustomRubrics(data.sectionId, assignmentId)
          )
          if (rubricErr) toast.error(tt.instructor_assignment.save_failed())
          store.navigateToInstructorClassPage(data.sectionId)
        }
        return onCreate?.()
      },
      [cubit, onCreate, persistCustomRubrics, store, tt.instructor_assignment]
    )

    const onSubmitDraft = useCallback(async () => {
      const values = form.getValues()
      values.assignedAt = values.assignedAt ?? undefined
      values.dueDate = values.dueDate ?? undefined

      // if grading is configured but invalid sum, do not submit
      if (values.configureGrading && !gradingSumValid) return

      // if custom rubrics are enabled but invalid, do not submit
      if (
        customRubricsRef.current.enabled &&
        !customRubricsRef.current.isValid
      ) {
        toast.error(tt.instructor_assignment.save_failed())
        return
      }

      const result = getDraftAssignmentSchema({
        tt,
        sectionStartDate: cubit.section.data.sectionStartDate,
        sectionEndDate: cubit.section.data.sectionEndDate,
      }).safeParse(values)

      if (result.success) {
        const assignmentGradingScalars = result.data.configureGrading
          ? scalarsRef.current
          : undefined

        const { expiresAt, assignmentOverdueAt } =
          calculateDBDatesFromFormDates({
            dueDate: result.data.dueDate,
            allowLateSubmissions: result.data.allowLateSubmissions,
            lateSubmissionCutoff: result.data.allowLateSubmissions
              ? result.data.lateSubmissionCutoff
              : undefined,
          })

        const [assignmentId, error] = await noTryAsync(async () => {
          return await cubit.createDraftSectionAssignment({
            slideDeckId: cubit.slideDeckId,
            assignmentGradingScalars,
            ...result.data,
            expiresAt,
            assignmentOverdueAt,
            peerReviewExcludesSelfReview:
              result.data.peerReviewExcludesSelfReview,
            peerReviewsHiddenFromReviewees:
              result.data.peerReviewsHiddenFromReviewees,
          })
        })

        if (error) {
          toast.error(tt.instructor_assignment.save_failed())
          throw error
        }

        if (assignmentId) {
          if (result.data.peerReviewsEnabled) {
            const questions = (result.data.peerReviewCriteria ?? []).map(
              (c, index) => ({
                prQuestion: c.prQuestion,
                prDescription: c.prDescription,
                prOrder: index,
              })
            )

            await cubit.setPeerReviewQuestionsForDraftAssignment({
              sectionId: result.data.sectionId,
              assignmentId,
              questions,
            })
          }
          const [, rubricErr] = await noTryAsync(() =>
            persistCustomRubrics(result.data.sectionId, assignmentId)
          )
          if (rubricErr) toast.error(tt.instructor_assignment.save_failed())
          store.navigateToInstructorClassPage(result.data.sectionId)
        }

        onCreate?.()
      } else {
        result.error.errors.forEach((error) => {
          form.setError(error.path[0] as keyof DraftAssignmentFormValues, {
            type: 'custom',
            message: error.message,
          })
        })
      }
    }, [
      cubit,
      form,
      gradingSumValid,
      onCreate,
      persistCustomRubrics,
      store,
      tt,
    ])

    const earlySecondaryButtonOverride = useMemo(
      () => ({
        text: tt.instructor_library.save_as_draft(),
        onClick: onSubmitDraft,
      }),
      [onSubmitDraft, tt.instructor_library]
    )

    const [showPublishConfirm, setShowPublishConfirm] = useState(false)
    const [formData, setFormData] = useState<AssignmentFormValues>()

    const onSubmit = useCallback(async (data: AssignmentFormValues) => {
      // Store form data and show confirmation dialog
      setFormData(data)
      setShowPublishConfirm(true)
    }, [])

    const { popDialog } = useDialogs()
    const [showDismissConfirmation, setShowDismissConfirmation] =
      useState(false)
    const hasSectionId = !!form.getValues().sectionId
    const dirtyFields = form.formState.dirtyFields
    const closeCallback = useCallback(() => {
      const isDirty = Object.keys(dirtyFields).length > 0 || customRubricsDirty
      // we cannot save if there is no section
      if (isDirty && hasSectionId) {
        setShowDismissConfirmation(true)
      } else {
        popDialog()
      }
    }, [dirtyFields, customRubricsDirty, hasSectionId, popDialog])

    return (
      <>
        <BaseAssignmentDialog
          sectionEndDate={dateTime(cubit.section.data.sectionEndDate)}
          sectionStartDate={dateTime(cubit.section.data.sectionStartDate)}
          closeCallback={closeCallback}
          dueDateInitialView={dueDateInitialView}
          form={form}
          onSubmit={onSubmit}
          releasedToStudents={false}
          hasBeenStarted={false}
          hasQuestions={hasQuestions}
          hasRubrics={hasRubrics}
          submitButtonText={tt.instructor_library.add_to_class()}
          scalarsRef={scalarsRef}
          earlySecondaryButtonOverride={earlySecondaryButtonOverride}
          sectionSelectComponent={
            <SectionSelect
              cubit={cubit.sectionSelectionCubit}
              onChange={(value) => {
                form.setValue('sectionId', value || '')
                form.trigger('sectionId')
              }}
              value={form.watch('sectionId')}
              hideCompletedSections={true}
              error={form.formState.errors.sectionId}
            />
          }
          gradingSumValidOverride={{
            gradingSum,
            gradingSumValid,
            setGradingSumCallback,
          }}
          slideDeckId={cubit.slideDeckId}
          advancedGradingSlot={
            canConfigureCustomRubrics ? (
              <ConfigureCustomRubrics
                rubrics={cubit.rubrics.models}
                roles={cubit.roles.models}
                configRef={customRubricsRef}
                onValidityChange={setCustomRubricsValid}
                onDirtyChange={setCustomRubricsDirty}
                weightsDialogOpen={weightsDialogOpen}
                onWeightsDialogClose={() => setWeightsDialogOpen(false)}
              />
            ) : undefined
          }
          onCustomizeWeights={() => setWeightsDialogOpen(true)}
          customRubricsValid={customRubricsValid}
        />

        {showDismissConfirmation && (
          <ConfirmationDialog
            inline
            title={tt.instructor_library.unsaved_changes_warning_title()}
            subtitle={tt.instructor_library.unsaved_changes_warning()}
            buttonText={tt.instructor_library.unsaved_changes_warning_save_draft()}
            buttonKind="accent"
            topIcon={
              <TriangleAlertIcon
                size={44}
                className="stroke-fixed-accent-color"
              />
            }
            secondaryButtonText={tt.instructor_library.unsaved_changes_warning_discard()}
            onConfirm={async () => {
              onSubmitDraft()
            }}
            dismiss={() => {
              setShowDismissConfirmation(false)
            }}
            overrideSecondaryClick={async () => {
              setShowDismissConfirmation(false)
              popDialog()
            }}
          />
        )}

        <ConfirmationDialog
          btnTestId="publish-button-confirm"
          open={showPublishConfirm}
          buttonKind="accent"
          dismiss={() => setShowPublishConfirm(false)}
          title={tt.instructor_assignment.publish_assignment()}
          inline={true}
          buttonText={tt.instructor_assignment.publish()}
          secondaryButtonText={tt.instructor_library.save_as_draft()}
          overrideSecondaryClick={onSubmitDraft}
          onConfirm={async () => {
            if (formData) {
              return submit(formData)
            }
            setShowPublishConfirm(false)
          }}
        />
      </>
    )
  }
)
